import React, { Fragment, ReactElement, ReactNode, Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import * as input from '../connectedForm/inputComponents';
import { default as Form } from '../connectedForm';
import getIn from '../connectedForm/utils/getIn';
import ItemInformation from './ItemInformation';
import Units from './Units';
import Formatters from '../grid-addons/formatters';
import { useDebouncedTemporarySave, useSingleFieldTemporarySave } from '../connectedForm/utils/handleTemporarySave';
import ColumnLabel from '../connectedForm/ColumnLabel';
import CustomerProductHistoryQuery from '../../graphql/queries/sales/CustomerProductHistoryQuery.graphql';
import GridContainer from '../connectedForm/GridContainer';
import stockTotalsQuery from '../../graphql/queries/product/StockTotalsQuery.graphql';
import EdataDetailsByProduct from '../../graphql/queries/product/EdataDetailsByProductQuery';
import EdataDataSheetDownloadMutation from '../../graphql/mutations/product/EdataDataSheetDownloadMutation';
import { maxMakeable } from '../../graphql/entities/product/Product';
import { ProductNoteType } from '../../graphql/entities';
import AppContext from '../../components/Context/AppContext';
import Row from '../connectedForm/Row';
import { InfoButton } from '../connectedForm/Button';
import { ContextMenu, ContextMenuTrigger, MenuItem } from 'react-contextmenu';
import styled from 'styled-components';
import iconMixin from '../../styles/utils/iconMixin';
import { useDispatch, useSelector } from 'react-redux';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import SalesOrderContext from '../Context/SalesOrderContext';
import { usePrecision } from '../../utils/hooks';
import { ViewContextProvider } from '../Context/ViewContext';
import { CustomDiscountInput } from './CustomDiscountInput';
import { Spinner } from '@fluentui/react/lib/Spinner';
import {
  CostViewPermission,
  FilterOperation,
  IAbstractLine,
  IAlternativeProduct,
  ICustomer,
  IEdataAssetDto,
  IEdataAssetLink,
  IEdataDetailsByProductQueryResult,
  IEdataProductDto,
  IFilterInput,
  IItemInformation,
  IProduct,
  IProductNotesQueryResult,
  IProductNotesQueryVariables,
  IPurchaseOrder,
  IPurchaseOrderLine,
  IQueryEdataDetailsByProductArgs,
  ISalesCredit,
  ISalesInvoice,
  ISalesInvoiceLine,
  ISalesOrder,
  ISalesOrderAttachment,
  ISalesOrderLine,
  ISupplier,
  ISupplierReturn,
  Maybe,
  OrderType,
  ProductClass,
  SalesOrderLineStatus,
} from '../../graphql/schema/p4types';
import BlurTextField, { BlurTextFieldType, IBlurTextFieldProps } from '../connectedForm/BlurTextField/BlurTextField';
import { ICommit, IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { IDropdownProps } from '../../@types/dropdown';
import { usePurchaseOrderCapability } from '../modals/usePurchaseOrderCapability';
import Legend from '../connectedForm/Legend/Legend';
import EditableDataGrid from '../EditableDataGrid/EditableDataGrid';
import { checkLineIsProcessed, hasAssignments, isFullyInvoiced, isFullyReceived } from '../../utils/LineUtils';
import { PriceDescriptionEditableField, PriceDescriptionField } from './PriceDescriptionField';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import ProductsStockLotWorkspace from '../../views/product/ProductsStockLotWorkspace';
import { useCompany, useSalesOrderConfig } from '../../utils/FetchConfig';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import WorkspaceModal from '../Workspace/WorkspaceModal';
import SalesOrderLineWorkspace from '../../views/sales/SalesOrderLineWorkspace';
import PurchaseOrderLineWorkspace from '../../views/purchases/PurchaseOrderLineWorkspace';
import { format } from 'date-fns';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { useId } from '@fluentui/react-hooks';
import ValueField from '../connectedForm/ValueField';
import MemoEditor, { IMemoEditorType, useMemoEditorProps } from '../connectedForm/MemoEditor/MemoEditor';
import ProductNotesWorkspaceQuery from '../../graphql/queries/product/ProductNotesWorkspaceQuery.graphql';
import { useSymbolFromCurrency } from '../../utils/CurrencyUtil';
import FluentTextField from '../FluentWrapper/FluentTextField';
import CurrencyContext from '../Context/CurrencyContext';
import { IDateFieldProps } from '../connectedForm/DateInput/DateInput';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import QuoteItemOrderHistoryTabContent from './LineDetails/QuoteItemOrderHistoryTabContent';
import { ComponentGrid } from './SalesOrderComponentGrid';
import { LineDetailsPanelAssemblyAssignments, LineDetailsPanelAssignments } from './LineDetailsPanelAssignments';
import { ICheckboxProps } from '@fluentui/react';
import EdataProductDetails from '../../views/product/EdataProductDetails';
import UrlFormatter from '../grid-addons/formatters/UrlFormatter';
import { cloneDeep } from '@apollo/client/utilities/common/cloneDeep';
import getClientMutationId from '../../utils/getClientMutationId';
import { BasicHeader } from '../../actions/BasicHeader';
import FluentModal from '../modals/FluentModal';
import ProductWarehouseUsageGrid from '../../views/product/ProductWarehouseUsageGrid';

const LazyProductAllocationWorkspace = React.lazy(() => import('../../views/product/ProductAllocationWorkspace'));
const LazyReservationWorkspace = React.lazy(() => import('../../views/product/ReservationWorkspace'));

const Div = styled.label`
  display: inline-block;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.inputLabel};
  width: 100%;
`;

const DivLeft = styled(Div)`
  text-align: start;
`;
const DivRight = styled(Div)`
  text-align: end;
`;

const Icon = styled.span`
  ${iconMixin('.925rem')};
  padding-left: 0.3125rem;
  vertical-align: middle;
  line-height: inherit;
  display: inline;
`;

const GreenIcon = styled(Icon)`
  color: #4caf50;
`;
const AmberIcon = styled(Icon)`
  color: #ffa000;
`;
const RedIcon = styled(Icon)`
  color: #d32f2f;
`;

const LuckinsHeader = styled.div`
  display: inline-block;
  height: 25px;
  background-color: #00b9af;
  color: #ffffff;
  padding: 3px;
  margin-top: 10px;
  margin-bottom: 7px;
  font-weight: 400;
  font-size: 13px;
`;

export const getLineDiscountInformation = (
  lastGrossPrice: number,
  lastPrice: number,
  pricePrecision: number | undefined,
  lastDiscountPercentage: number
) => {
  let discountInformation = '';
  if (lastGrossPrice && lastPrice && lastDiscountPercentage > 0) {
    const lastGrossPriceFormatted = lastGrossPrice.toFixed(pricePrecision);
    const discountAppliedFormatted = (lastGrossPrice - lastPrice).toFixed(pricePrecision);
    discountInformation = `(£${lastGrossPriceFormatted} - £${discountAppliedFormatted} or ${lastDiscountPercentage}%)`;
  }

  return discountInformation;
};

const INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE = 'INCLUDE_ARCHIVED_ASSIGNMENTS_COOKIE';
const INCLUDE_ARCHIVED_ASSEMBLY_ASSIGNMENTS_COOKIE = 'INCLUDE_ARCHIVED_ASSEMBLY_ASSIGNMENTS_COOKIE';

const AltProductsSection = ({
  altProducts,
  legend,
  rowIndex,
  orderLines,
}: {
  altProducts: Array<IAlternativeProduct>;
  legend: string;
  rowIndex: number;
  orderLines: Array<Maybe<IAbstractLine>>;
}) => {
  if (altProducts.length === 0) return null;
  return (
    <>
      <Form.Legend label={legend} />
      {altProducts.map((alternativeProduct) => (
        <AlternativeProductRow
          key={`cell_${alternativeProduct.id}`}
          alternativeProduct={alternativeProduct}
          rowIndex={rowIndex}
          orderLines={orderLines}
        />
      ))}
    </>
  );
};

const priceBandFieldMap = {
  SALES_PRICE_MINIMUM: 'minimumPrice',
  SALES_PRICE_1: 'sellingPrice1',
  SALES_PRICE_2: 'sellingPrice2',
  SALES_PRICE_3: 'sellingPrice3',
  SALES_PRICE_4: 'sellingPrice4',
  SALES_PRICE_5: 'sellingPrice5',
  SALES_PRICE_6: 'sellingPrice6',
  SALES_PRICE_7: 'sellingPrice7',
  SALES_PRICE_8: 'sellingPrice8',
  SALES_PRICE_9: 'sellingPrice9',
  SALES_PRICE_10: 'sellingPrice10',
};

const AlternativeProductRow = ({
  alternativeProduct,
  rowIndex,
  orderLines,
}: {
  alternativeProduct: IAlternativeProduct;
  rowIndex: number;
  orderLines: Array<Maybe<ISalesOrderLine>>;
}) => {
  const { product, type } = alternativeProduct;
  const { reference, description, sellingPrices, freeStock, level } = product!;

  const { onRowUpdated } = useContext(SalesOrderContext);
  const connectedForm = useContext(ConnectedFormContext);
  const temporarySave = useSingleFieldTemporarySave();

  const priceBand =
    priceBandFieldMap[connectedForm?.values?.defaultPriceBand as keyof typeof priceBandFieldMap] || 'sellingPrice1';

  const addItem = (quantity = 1) => {
    const newLine: { product: Maybe<IProduct>; quantity: number; uuid: string } = {
      product: alternativeProduct.product,
      quantity: quantity,
      uuid: uuIdGenerator(),
    };
    const newLineRowIdx: number = alternativeProduct.type === 'UPSELL' ? rowIndex : orderLines.length;

    temporarySave(`orderLines.${newLineRowIdx}`, newLine, {
      postSaveCallback: (saveResult) => {
        const tempSavedLine = saveResult?.data?.salesOrderTemporaryUpdate?.result?.orderLines?.[newLineRowIdx];
        if (tempSavedLine) {
          //Trigger onRowSelect to display shortage modal, if required.
          onRowUpdated?.({
            cellKey: 'product',
            updated: tempSavedLine,
            original: {} as ISalesOrderLine,
            rowIdx: newLineRowIdx,
            cellKeys: ['product'],
          } as ICommit<ISalesOrderLine>);
        }
      },
    });
  };

  const onAdd = () => {
    if (connectedForm.values?.archived) return;
    const quantity = type === 'UPSELL' ? getIn(orderLines, [rowIndex, 'quantity']) : 1;
    addItem(quantity);
  };

  let stockIcon = <RedIcon>{'cancel'}</RedIcon>;
  if (freeStock > 0) {
    stockIcon = <GreenIcon>{'check_circle'}</GreenIcon>;
  } else if (level > 0) {
    stockIcon = <AmberIcon>{'adjust'}</AmberIcon>;
  }

  return (
    <div
      style={{
        borderBottomStyle: 'solid',
        borderBottomWidth: '1px',
        borderBottomColor: '#e0e0e0',
        paddingTop: '5px',
        paddingBottom: '5px',
      }}
    >
      <Row>
        <Form.Fieldset cols={4 / 6}>
          <DivLeft>
            <div
              style={{
                color: '#919191',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                display: 'inline-block',
                maxWidth: '100%',
              }}
            >
              {reference}
            </div>
            <br />
            {description}
          </DivLeft>
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 6}>
          <DivRight>
            <span style={{ color: '#919191' }}>{freeStock}</span>
            {stockIcon}
            <br />
            <span>{`£${sellingPrices?.[priceBand]?.toFixed(2) || '0.00'}`}</span>
          </DivRight>
        </Form.Fieldset>
        <ContextMenuTrigger id={`alternativeProductsMenu${alternativeProduct.id}`}>
          <InfoButton inline={true} small={true} onClick={onAdd} disabled={connectedForm.values?.archived}>
            {type === 'UPSELL' ? 'Replace' : 'Add'}
          </InfoButton>
        </ContextMenuTrigger>
      </Row>
      <ContextMenu
        key={`alternativeProductsMenu${alternativeProduct.id}`}
        id={`alternativeProductsMenu${alternativeProduct.id}`}
      >
        {[2, 3, 4, 5, 10].map((amount) => (
          <MenuItem key={`_${amount}`} onClick={() => addItem(amount)}>{`Add ${amount} to Order`}</MenuItem>
        ))}
      </ContextMenu>
    </div>
  );
};

const NettPriceInformation = ({ orderLine, currencyCode, excludingVat = false, labelCols, inputCols, type }) => {
  if (!orderLine.nettSellingPrice) return null;
  return (
    <PriceDescriptionField
      label={`Net ${type === 'SALES' ? 'Selling' : 'Cost'} Price ${excludingVat ? '(Excl VAT)' : ''}`}
      price={excludingVat ? orderLine.nettSellingPriceExcludingVat : orderLine.nettSellingPrice}
      unitQuantity={orderLine.sellingUnitQuantity}
      unitDescription={orderLine.sellingUnitDescription}
      currencyCode={currencyCode}
      labelCols={labelCols}
      inputCols={inputCols}
      type={'currency'}
      valueType={type === 'SALES' ? 'sales' : 'cost'}
      testId="net-price-field"
    />
  );
};

const CostPriceInformation = ({
  orderLine,
  readOnly,
  fieldPrefix,
  currencyCode,
}: {
  orderLine: ISalesOrderLine;
  readOnly?: boolean;
  fieldPrefix: string;
  currencyCode: Maybe<string>;
}) => {
  const app = useContext(AppContext);
  const salesOrderConfig = useSalesOrderConfig();

  if (!{}.hasOwnProperty.call(orderLine, 'displayCostPrice')) return null;

  if (orderLine.product && orderLine.product.template) {
    return (
      <Form.Fieldset cols={1}>
        <Form.Legend label="Cost Price" />
        <Form.Field
          component={input.BlurTextField}
          dataid={`${fieldPrefix}.displayCostPrice`}
          label="Cost price"
          temporary={true}
          readOnly={readOnly}
          type={'currency'}
        />
        <Form.Field
          component={input.BlurTextField}
          dataid={`${fieldPrefix}.costUnitQuantity`}
          label="Unit Quantity"
          temporary={true}
          readOnly={readOnly}
        />
        <Form.Field
          component={input.BlurTextField}
          dataid={`${fieldPrefix}.costUnitDescription`}
          label="Unit Description"
          temporary={true}
          readOnly={readOnly}
        />
      </Form.Fieldset>
    );
  }

  if (
    !app?.fullUserDetails?.companyUser?.costViewPermission ||
    app.fullUserDetails.companyUser.costViewPermission === CostViewPermission.None
  ) {
    return null;
  }

  const costPriceReadOnly =
    readOnly ||
    (orderLine.salesOrderLineStatus?.findIndex((status) => SalesOrderLineStatus.Dispatched === status) ?? -1) >= 0;

  const costPriceHint = `Order config - "Cost for margin" is set to ${salesOrderConfig?.costForMargin} and
       "Set line cost price from purchase order" is set to ${salesOrderConfig?.updateLineCostPriceFromPurchaseOrder}`;

  const costPriceDescription = costPriceReadOnly ? (
    <PriceDescriptionField
      key={'costPrice'}
      label={'Cost Price'}
      price={orderLine.displayCostPrice}
      unitQuantity={orderLine.costUnitQuantity}
      unitDescription={orderLine.costUnitDescription}
      currencyCode={currencyCode}
      type={'currency'}
      valueType="cost"
      labelProps={{ hint: costPriceHint }}
      testId="cost-price-field"
    />
  ) : (
    <PriceDescriptionEditableField
      label={'Cost Price'}
      lineDataid={fieldPrefix}
      unitQuantity={orderLine.costUnitQuantity}
      unitDescription={orderLine.costUnitDescription}
      type={'currency'}
      labelProps={{ hint: costPriceHint }}
      valueType="cost"
    />
  );

  const mainSupplierCostPrice =
    orderLine.product?.mainSupplierDetails?.costPrices?.costPrice1 &&
    app?.fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All ? (
      <CurrencyContext.Provider value={{ currency: orderLine.product?.mainSupplierDetails?.supplier?.currency }}>
        <PriceDescriptionField
          label={'Main Supplier Cost Price'}
          price={orderLine.product.mainSupplierDetails.costPrices.costPrice1}
          unitQuantity={orderLine.product.costUnitQuantity}
          unitDescription={orderLine.product.costUnitDescription}
          currencyCode={orderLine.product?.mainSupplierDetails?.supplier?.currency?.isoCode?.codeAlpha3}
          type={'currency'}
          valueType="cost"
          testId="main-supplier-cost-price-field"
        />
      </CurrencyContext.Provider>
    ) : null;

  let equivalentCostPriceDescription: ReactElement | ReactNode = null;

  if (orderLine.sellingUnitQuantity !== orderLine.costUnitQuantity) {
    let equivalentCost = orderLine.displayCostPrice / orderLine.costUnitQuantity;
    equivalentCost *= orderLine.sellingUnitQuantity;

    //Apply alternate selling unit to cost for display.
    const activeSellingUnit = orderLine.activeSellingUnit;
    if (activeSellingUnit?.quantityFactor) {
      if (activeSellingUnit.factorDirection === 'FROM_BASE_UNIT') {
        equivalentCost *= activeSellingUnit.quantityFactor;
      } else {
        equivalentCost /= activeSellingUnit.quantityFactor;
      }
    }

    equivalentCostPriceDescription = (
      <PriceDescriptionField
        key={'equivalentCostPrice'}
        label={'Equivalent Cost Price'}
        price={equivalentCost}
        unitQuantity={orderLine.sellingUnitQuantity}
        unitDescription={orderLine.sellingUnitDescription}
        currencyCode={currencyCode}
        type={'currency'}
        valueType="cost"
        testId="equiv-cost-price-field"
      />
    );
  }
  return (
    <>
      {costPriceDescription}
      {equivalentCostPriceDescription}
      <Form.Field
        dataid={`${fieldPrefix}.costPriceOrigin`}
        component={BlurTextField}
        label={'Cost Price Origin'}
        readOnly={true}
      />
      {mainSupplierCostPrice}
    </>
  );
};

const MinimumPriceInformation = ({ orderLine, currencyCode }) => {
  const potentialPrices = orderLine?.potentialPrices || [];
  const minimumPotential = potentialPrices.find((potential) => potential.description === 'Minimum');

  //Set minimum price to minimum from potential (Calculated) or product minimum selling price (adjusted according to AUM).
  let minimumPrice;
  if (minimumPotential?.nettPrice !== null && minimumPotential?.nettPrice !== undefined) {
    minimumPrice = minimumPotential?.nettPrice;
  } else if (orderLine.activeSellingUnit) {
    const activeSellingUnit = orderLine.activeSellingUnit;
    if (activeSellingUnit?.quantityFactor) {
      if (activeSellingUnit.factorDirection === 'FROM_BASE_UNIT') {
        minimumPrice = orderLine?.product?.sellingPrices?.minimumPrice * activeSellingUnit.quantityFactor;
      } else {
        minimumPrice = orderLine?.product?.sellingPrices?.minimumPrice / activeSellingUnit.quantityFactor;
      }
    }
  } else {
    minimumPrice = orderLine?.product?.sellingPrices?.minimumPrice;
  }

  if (!minimumPrice) return null;

  return (
    <PriceDescriptionField
      label="Minimum Price"
      price={minimumPrice}
      unitQuantity={orderLine.sellingUnitQuantity}
      unitDescription={orderLine.sellingUnitDescription}
      currencyCode={currencyCode}
      type="currency"
      valueType="sales"
      testId="minimum-price-field"
    />
  );
};

const AlternativeProducts = ({
  alternativeProducts,
  rowIndex,
  orderLines,
}: {
  alternativeProducts: Array<Maybe<IAlternativeProduct>>;
  rowIndex: number;
  orderLines: Array<Maybe<IAbstractLine>>;
}) => {
  const upsell: Array<IAlternativeProduct> = [];
  const related: Array<IAlternativeProduct> = [];
  const crosssell: Array<IAlternativeProduct> = [];

  alternativeProducts.forEach((alt) => {
    switch (alt?.type) {
      case 'UPSELL':
        upsell.push(alt);
        break;
      case 'RELATED':
        related.push(alt);
        break;
      case 'CROSSSELL':
        crosssell.push(alt);
        break;
    }
  });

  return (
    <Form.Row>
      <Form.Fieldset cols={1 / 3}>
        <AltProductsSection altProducts={related} legend={'Related'} rowIndex={rowIndex} orderLines={orderLines} />
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 3}>
        <AltProductsSection altProducts={upsell} legend={'Upsell'} rowIndex={rowIndex} orderLines={orderLines} />
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 3}>
        <AltProductsSection altProducts={crosssell} legend={'Cross Sell'} rowIndex={rowIndex} orderLines={orderLines} />
      </Form.Fieldset>
    </Form.Row>
  );
};

type IPurchaseOrderEditComponentProps = {
  onChange?: Function;
  value?: ISalesOrderLine;
  newPurchaseOrder: IPurchaseOrder;
};

//Tab for accessing generated PO's for editing and/or making corrections on save.
const PurchaseOrderEditComponent = ({ onChange, value, newPurchaseOrder }: IPurchaseOrderEditComponentProps) => {
  const { setPurchaseOrderState, modals: purchaseOrderModal } = usePurchaseOrderCapability({
    onChange,
    onClose: () => {},
    value,
  });

  if (!newPurchaseOrder) return <div>{'No Purchase Order Available'}</div>;

  return (
    <Form.Row>
      {purchaseOrderModal}
      <Form.Fieldset cols={1}>
        <Legend label={`Edit PO for supplier ${newPurchaseOrder.supplier?.name}`} />
        <InfoButton onClick={() => setPurchaseOrderState({ modalOpen: true, initialValues: newPurchaseOrder })}>
          {'View/Edit Purchase Order'}
        </InfoButton>
      </Form.Fieldset>
    </Form.Row>
  );
};

const CustomerHistory = ({ customer, product }) => {
  const { loading, data } = useQuery(CustomerProductHistoryQuery, {
    variables: {
      customerId: customer?.id,
      productId: product?.id,
    },
  });

  if (loading) return <div>{'Loading...'}</div>;
  if (!data) return <div>{'No History Available'}</div>;
  if (!data.data) return <div>{'No History Available'}</div>;

  const columns: Array<IEditableDataGridColumnDef> = [];
  const singleRow = {};
  data.data.forEach((historicValue) => {
    columns.push({
      name: historicValue.title,
      key: historicValue.key,
      editable: false,
      formatter: 'numeric',
      options: {
        format: { decimals: product?.precision || 0 },
      },
      resizable: true,
    });
    singleRow[historicValue.key] = historicValue.quantity || 0;
  });
  return (
    <GridContainer>
      <EditableDataGrid dataid="orderHistory" columns={columns} listData={[singleRow]} rowsCount={1} />
    </GridContainer>
  );
};

const StockTotals = ({ product }) => {
  const { loading, data } = useQuery(stockTotalsQuery, {
    variables: { productId: product?.id },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  const decimals = product?.precision || 0;

  const stockTotalColumnMeta = [
    {
      name: 'Warehouse',
      key: 'warehouse.description',
      editable: false,
      resizable: true,
    },
    {
      name: 'Default Bin',
      key: 'warehouse.defaultBin.location',
      editable: false,
      resizable: true,
    },
    {
      name: 'Level',
      key: 'level',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals },
      },
    },
    {
      name: 'Free Stock',
      key: 'freeStock',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals },
      },
    },
    {
      name: 'Makeable',
      key: 'makeable',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals },
        infinityLimit: maxMakeable,
      },
    },
    {
      name: 'Allocated',
      key: 'allocated',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals },
      },
    },
    {
      name: 'Production Required',
      key: 'productionRequired',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals },
      },
    },
    {
      name: 'In Production',
      key: 'inProduction',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals },
      },
    },
    {
      name: 'Issued',
      key: 'issued',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals },
      },
    },
    {
      name: 'On Order',
      key: 'onOrder',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals },
      },
    },
  ];

  if (loading) {
    return null;
  }

  const stockLevelData = (data && data.data) || [];
  return (
    <GridContainer>
      <EditableDataGrid
        columns={stockTotalColumnMeta}
        addBlankRow={false}
        enableCellSelect={true}
        enableCellAutoFocus={false}
        listData={stockLevelData}
        rowsCount={stockLevelData.length}
        contentHeight={window.innerHeight - 250}
        initialSortColumn="warehouse.description"
        initialSortDir="ASC"
      />
    </GridContainer>
  );
};

type IEdataAssetLinksGridProps = {
  edataAssetLinks: Array<Maybe<IEdataAssetLink>> | undefined | null;
};

const EdataAssetLinksGrid = ({ edataAssetLinks }: IEdataAssetLinksGridProps) => {
  const edataAssetColumns: IEditableDataGridColumnDef = [
    {
      name: 'Name',
      key: 'assetDisplayName',
      editable: true,
      resizable: true,
    },
    {
      name: 'Link',
      key: 'assetSource',
      editable: false,
      resizable: true,
      formatter: UrlFormatter,
    },
  ];

  const links = edataAssetLinks || [];

  return (
    <GridContainer>
      <EditableDataGrid
        columns={edataAssetColumns}
        addBlankRow={false}
        enableCellSelect={true}
        enableCellAutoFocus={false}
        listData={edataAssetLinks || []}
        rowsCount={links.length}
        contentHeight={window.innerHeight - 250}
        initialSortColumn="assetDisplayName"
        initialSortDir="ASC"
      />
    </GridContainer>
  );
};

const VolumeComponent = ({ orderLine }: { orderLine: Maybe<ISalesOrderLine> }) => {
  const packagingDimensions = orderLine?.product?.packagingDimensions;
  const lengthUnitOfMeasure = packagingDimensions?.physicalDimensionDescription?.lengthUnitOfMeasure?.toLowerCase();

  const dimensions = lengthUnitOfMeasure
    ? {
        length: `${packagingDimensions?.length} ${lengthUnitOfMeasure}`,
        width: `${packagingDimensions?.width} ${lengthUnitOfMeasure}`,
        depth: `${packagingDimensions?.depth} ${lengthUnitOfMeasure}`,
        volume: `${packagingDimensions?.volume} m³`,
      }
    : {};

  return (
    <Form.Fieldset cols={1 / 2}>
      <Form.Legend label="Packaging Volume" />
      <Form.Row>
        {Object.entries(dimensions).map(([side, dimension]) => {
          return (
            <Fragment key={`${side}-${dimension}`}>
              <Form.Fieldset cols={1 / 6} collapsed={true}>
                <ColumnLabel key={`${side}Key`} label={side[0].toUpperCase() + side.slice(1)} />
                <input.BlurTextField
                  key={`${side}Value`}
                  value={dimension || 0}
                  includeLabel={false}
                  readOnly={true}
                  data-testid={`packaging-volume-${dimension}`}
                />
              </Form.Fieldset>
            </Fragment>
          );
        })}
      </Form.Row>
    </Form.Fieldset>
  );
};

const AllocationsTabContent = ({
  field,
  orderLine,
}: {
  field: 'orderLines' | 'invoiceLines';
  orderLine: ISalesOrderLine | ISalesInvoiceLine;
}) => {
  const allocationFilters: Array<IFilterInput> = [
    {
      filterParameters: {
        field: 'orderLine.id',
        value: field === 'orderLines' ? orderLine.id : (orderLine as ISalesInvoiceLine).salesOrderLine?.id,
      },
      filterOperation: FilterOperation.Equals,
    },
    {
      filterParameters: {
        field: 'cancelled',
        value: 'false',
      },
      filterOperation: FilterOperation.Equals,
    },
  ];

  return (
    <Suspense fallback={<ProgressIndicator />}>
      <GridContainer>
        <LazyProductAllocationWorkspace
          embeddedView={true}
          predefinedFilters={allocationFilters}
          viewScreenName="/salesLedger/salesInvoice/productAllocation"
          contentHeight={300}
        />
      </GridContainer>
    </Suspense>
  );
};

const ReservationsTabContent = ({ orderLine }: { orderLine: ISalesOrderLine }) => {
  const reservationFilters: Array<IFilterInput> = [
    {
      filterParameters: {
        field: 'salesOrderLine.id',
        value: orderLine.id,
      },
      filterOperation: FilterOperation.Equals,
    },
    {
      filterParameters: {
        field: 'processed',
        value: 'false',
      },
      filterOperation: FilterOperation.Equals,
    },
    {
      filterParameters: {
        field: 'quantity',
        value: '0',
      },
      filterOperation: FilterOperation.GreaterThan,
    },
  ];

  return (
    <Suspense fallback={<ProgressIndicator />}>
      <GridContainer>
        <LazyReservationWorkspace
          embeddedView={true}
          predefinedFilters={reservationFilters}
          viewScreenName="/salesLedger/salesInvoice/productReservation"
        />
      </GridContainer>
    </Suspense>
  );
};

const LastOrderInformationHaggleTab = ({
  itemInformation,
  commonRecord,
  product,
  type,
}: {
  itemInformation: Maybe<IItemInformation>;
  commonRecord: ICustomer | ISupplier;
  product: IProduct;
  type: 'SALES' | 'PURCHASE';
}) => {
  const pricePrecision = usePrecision(type === 'SALES' ? 'sales' : 'cost');
  const costPermission = useSelector((store: any) => store.app?.fullUserDetails?.companyUser?.costViewPermission);
  const currencySymbol = useSymbolFromCurrency(BlurTextFieldType.Currency);

  const [salesOrderLineState, setSalesOrderLineState] = useState<{ modalOpen: boolean; filters?: Array<IFilterInput> }>(
    { modalOpen: false }
  );
  const [purchaseOrderLineState, setPurchaseOrderLineState] = useState<{
    modalOpen: boolean;
    filters?: Array<IFilterInput>;
  }>({ modalOpen: false });
  const lastOrderTooltipId = useId('lastOrderTooltipId');

  const displaySalesOrderLineModal = (filters: Array<IFilterInput>) => {
    setSalesOrderLineState({ modalOpen: true, filters: filters });
  };
  const displayPurchaseOrderLineModal = (filters: Array<IFilterInput>) => {
    setPurchaseOrderLineState({ modalOpen: true, filters: filters });
  };

  const onLastSalesOrderClick = useCallback(() => {
    displaySalesOrderLineModal([
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'salesOrder.customer.id',
          value: commonRecord.id,
        },
      },
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'product.id',
          value: product.id,
        },
      },
    ]);
  }, [commonRecord?.id, product.id]);

  const onLastPurchaseOrderClick = useCallback(() => {
    displayPurchaseOrderLineModal([
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'purchaseOrder.supplier.id',
          value: commonRecord.id,
        },
      },
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'product.id',
          value: product.id,
        },
      },
    ]);
  }, [commonRecord?.id, product.id]);

  if (!itemInformation) return null;
  const {
    lastPrice,
    lastPriceDate,
    lastPriceUnitDescription,
    lastPriceOrderQuantity,
    lastGrossPrice,
    lastDiscountPercentage,
  } = itemInformation;
  if (!lastPriceOrderQuantity || lastPriceOrderQuantity <= 0) {
    return null;
  }

  const discountInformation = getLineDiscountInformation(
    lastGrossPrice,
    lastPrice,
    pricePrecision,
    lastDiscountPercentage
  );

  return (
    <>
      <WorkspaceModal
        component={SalesOrderLineWorkspace}
        viewScreenName={'/sales/SalesOrderLineWorkspace/Modal'}
        modalState={salesOrderLineState}
        closeModal={() => {
          setSalesOrderLineState({ modalOpen: false });
        }}
      />
      <WorkspaceModal
        component={PurchaseOrderLineWorkspace}
        viewScreenName={'/Purchasing/OrderLines/Modal/'}
        modalState={purchaseOrderLineState}
        closeModal={() => {
          setPurchaseOrderLineState({ modalOpen: false });
        }}
      />
      <TooltipHost
        content={`View ${type === 'SALES' ? 'Sales' : 'Purchase'} Order Lines for this ${
          type === 'SALES' ? 'Customer' : 'Supplier'
        }/Product`}
        id={lastOrderTooltipId}
        calloutProps={{ gapSpace: 0 }}
      >
        <div
          style={{ cursor: 'pointer' }}
          onClick={type === 'SALES' ? onLastSalesOrderClick : onLastPurchaseOrderClick}
        >
          <ValueField
            value={
              costPermission === CostViewPermission.All || type === 'SALES'
                ? `${lastPriceOrderQuantity} at ${currencySymbol}${lastPrice?.toFixed(
                    pricePrecision
                  )} ${discountInformation} ${(lastPriceUnitDescription || 'each')
                    .toString()
                    .toLowerCase()} on ${format(new Date(lastPriceDate), 'do MMM yyyy')}`
                : `${lastPriceOrderQuantity} on ${format(new Date(lastPriceDate), 'do MMM yyyy')}`
            }
            label="Last Order"
            readOnly={true}
            labelCols={2 / 5}
            inputCols={3 / 5}
            data-testid={'haggle-tab-last-order'}
            align={'left'}
            link={true}
          />
        </div>
      </TooltipHost>
    </>
  );
};

type ILineDetailsPanelProps = {
  itemInformationQuery: DocumentNode;
  orderType?: OrderType;
  colIdx?: Maybe<number>;
  readOnly?: boolean;
  active?: string;
  type?: 'SALES' | 'PURCHASE';
  orderLines?: Maybe<Array<Maybe<IAbstractLine>>>;
  field: 'orderLines' | 'invoiceLines' | string;
  rowIndex: Maybe<number>;
  currencyCode: Maybe<string>;
  isUseAcknowledgedDeliveryDate?: Maybe<boolean>;
};

// eslint-disable-next-line complexity
const LineDetailsPanel = ({
  itemInformationQuery,
  orderType,
  colIdx,
  active,
  type = 'SALES',
  orderLines,
  field = 'orderLines',
  rowIndex,
  currencyCode,
  isUseAcknowledgedDeliveryDate,
}: ILineDetailsPanelProps) => {
  const app = useContext(AppContext);
  const performTemporarySave = useDebouncedTemporarySave(false, 1000); //Slow-ish timer to avoid cutting off the decimal point on discount inputs.
  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);
  const customer = connectedForm.values?.customer;
  const supplier = connectedForm.values?.supplier;
  const commonRecord = type === 'SALES' ? customer : supplier;
  const orderLine: ISalesOrderLine =
    rowIndex !== undefined && rowIndex !== null && orderLines ? orderLines[rowIndex] : null;
  const currencySymbol = useSymbolFromCurrency(BlurTextFieldType.Currency);
  const [processingAttachment, setProcessingAttachment] = useState(false);

  const eDataResult = useQuery<IEdataDetailsByProductQueryResult, IQueryEdataDetailsByProductArgs>(
    EdataDetailsByProduct,
    {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      variables: { productId: orderLine?.product?.id },
      skip: !connectedForm.initialized || !orderLine?.product?.id,
    }
  );
  const [edataDataSheetDownload] = useMutation(EdataDataSheetDownloadMutation);

  const eDataProductId = !eDataResult.loading && eDataResult.data?.data ? eDataResult.data.data.edataProductId : null;

  const productMemoEditorProps = useMemoEditorProps<IProductNotesQueryResult, IProductNotesQueryVariables>(
    ProductNotesWorkspaceQuery,
    IMemoEditorType.Product,
    {
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'product.id',
            value: orderLine?.product?.id,
          },
        },
      ],
      skip: !orderLine?.product?.id,
    }
  );

  const itemInformationVariables = useMemo(() => {
    return {
      orderLine: normalizeVariables(orderLine),
      commonRecord: normalizeVariables(commonRecord),
    };
    //Don't include orderLine/commonRecord directly, they change too frequently.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderLine?.uuid, orderLine?.product?.uuid, orderLine?.warehouse?.uuid, commonRecord?.uuid]);

  const { data: itemInformationData } = useQuery(itemInformationQuery, {
    variables: itemInformationVariables,
    skip: !orderLine,
  });

  const components = orderLines?.[rowIndex || 0]?.components;
  const salesOrderLineStatuses: Maybe<Array<Maybe<SalesOrderLineStatus>>> = (
    orderLines?.[rowIndex || 0] as Maybe<ISalesOrderLine>
  )?.salesOrderLineStatus;

  const isProcessed: boolean = checkLineIsProcessed(salesOrderLineStatuses);

  const readOnly = connectedForm.readOnly || (type === 'SALES' && isFullyInvoiced(orderLine as ISalesOrderLine));

  const company = useCompany();
  const isWindsorFrameWorkEnabled = company?.windsorFrameworkEnabled ?? false;

  const viewContext = useMemo(
    () => ({
      uniqueName: 'LineDetailsPanel',
    }),
    []
  );

  if (
    !orderLines ||
    rowIndex === null ||
    rowIndex === undefined ||
    !orderLines[rowIndex] ||
    !orderLines[rowIndex]?.product
  ) {
    return null;
  }
  const { product, uuid: orderLineUuid, warehouse } = orderLine as IAbstractLine;

  const newPurchaseOrder: IPurchaseOrder = !!orderLineUuid
    ? connectedForm.supportingData?.salesLineNewPurchaseOrderMap?.[orderLineUuid as string]
    : null;

  const labelAddon = currencyCode ? ` (${currencyCode})` : undefined;

  const priceSelectionPermission = app.fullUserDetails?.companyUser?.priceSelectionPermission;

  const pricesReadOnly = !(!readOnly && priceSelectionPermission === 'MANUAL');

  const alternativeProducts =
    orderLines[rowIndex]?.product?.supersessions?.filter((entry) => entry?.type !== 'SUPERSESSION') || [];

  const vatInclusive = connectedForm?.values?.vatInclusive;

  const componentsReadOnly = readOnly || isProcessed || product?.productClass === ProductClass.Assembly;

  const itemInformation = itemInformationData?.data as Maybe<IItemInformation>;

  const attachAsset = (asset: IEdataAssetDto) => {
    const assetLink: IEdataAssetLink = {
      assetSource: asset.assetSource,
      assetDisplayName: asset.assetDescription ? asset.assetDescription : asset.assetFilename,
    };

    const updatedAssets = orderLine?.edataAssetLinks ? cloneDeep(orderLine?.edataAssetLinks) : [];
    updatedAssets.push(assetLink);

    dispatch(connectedForm.change(`${field}.${rowIndex}.edataAssetLinks`, updatedAssets, uuIdGenerator()));
  };

  const attachEdataSheet = (edataProduct: IEdataProductDto) => {
    setProcessingAttachment(true);
    edataDataSheetDownload({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          gtin: edataProduct.gtin,
          filename: edataProduct.manufacturerPartNumber,
        },
      },
    }).then((response) => {
      const salesOrderAttachment: ISalesOrderAttachment = {
        fileName: response.data.edataDataSheetDownload.filename,
        includeOnDocuments: true,
      };

      const updatedAttachments = connectedForm?.values?.attachments ? cloneDeep(connectedForm.values.attachments) : [];
      updatedAttachments.push(salesOrderAttachment);

      dispatch(connectedForm.change('attachments', updatedAttachments, uuIdGenerator()));
      setProcessingAttachment(false);
    });
  };

  const stockLotsPredefinedFilters = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'stock.product.id',
        value: product?.id,
      },
    },
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'stock.binLocation.warehouse.id',
        value: orderLine?.warehouse?.id,
      },
    },
  ];

  const stockLotsWorkspaceColumns = ['stock.binLocation.location', 'level', 'freeStock', 'allocated', 'issued'];

  if (product?.nonReplenishable) {
    stockLotsWorkspaceColumns.push(...['originalLevel', 'consumed']);
  }

  if (
    app?.fullUserDetails?.companyUser?.costViewPermission &&
    app.fullUserDetails.companyUser.costViewPermission === CostViewPermission.All
  ) {
    stockLotsWorkspaceColumns.push('averageCostPrice');
  }

  // Cost view permissions for the purchasing module
  const showCostFieldsPurchasing: boolean =
    !type || type !== 'PURCHASE' || app?.fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All;

  let disableVatCategoryEditsForPurchaseOrders: boolean = false;
  let disableDiscountTermsEditsForPurchaseOrders: boolean = false;
  const line: Maybe<IAbstractLine> = orderLines[rowIndex];
  if (line && line.__typename === 'PurchaseOrderLine') {
    const purchaseOrderLine: IPurchaseOrderLine = line as IPurchaseOrderLine;
    if (purchaseOrderLine.invoicedQuantity > 0) {
      // VAT product category should be read only for part invoiced purchase order lines
      disableVatCategoryEditsForPurchaseOrders = true;
    }
    if (
      purchaseOrderLine.invoicedQuantity >= purchaseOrderLine.standardUnitQuantity &&
      purchaseOrderLine.invoicedQuantity === purchaseOrderLine.received
    ) {
      // Discount terms should be read only for fully invoiced purchase order lines
      disableDiscountTermsEditsForPurchaseOrders = true;
    }
  }

  return (
    <ViewContextProvider value={viewContext}>
      <FluentModal isOpen={processingAttachment}>
        <BasicHeader>{'Attaching Edata Data Sheet'}</BasicHeader>
        <Spinner label={'Processing Data Sheet'} />
      </FluentModal>
      <Form.Row>
        <Form.Fieldset cols={1}>
          <Form.TabPanel
            switchTabs={!!colIdx}
            active={active}
            onlyRenderWhenActive={true}
            screenName={`${connectedForm?.entity?.screenReference}/LineDetails`}
          >
            <Form.TabContent id="info" label="General">
              <ItemInformation
                key={`item_information_${product?.id}`}
                rowIndex={rowIndex}
                image={product?.image?.fileName}
                commonRecord={commonRecord}
                product={product!}
                headerEntity={
                  connectedForm.values as ISalesOrder | IPurchaseOrder | ISalesInvoice | ISalesCredit | ISupplierReturn
                }
                type={type}
                itemInformation={itemInformation}
                showCostFieldsPurchasing={showCostFieldsPurchasing}
              />
            </Form.TabContent>
            {eDataProductId ? (
              <Form.TabContent id="edata" label="EData">
                <EdataProductDetails
                  providedId={eDataProductId}
                  onAssetAttach={attachAsset}
                  onDataSheetAttach={attachEdataSheet}
                  enquiryMode={true}
                />
              </Form.TabContent>
            ) : null}
            {orderLine.edataAssetLinks && orderLine.edataAssetLinks.length > 0 ? (
              <Form.TabContent id="edataAssetLinks" label="Asset Links">
                <EdataAssetLinksGrid edataAssetLinks={orderLine.edataAssetLinks} />
              </Form.TabContent>
            ) : null}
            <Form.TabContent id="details" label="Details">
              <Form.Fieldset cols={2 / 4}>
                <Form.Field
                  dataid={`${field}.${rowIndex}.warehouse`}
                  component={input.Dropdown}
                  label="Warehouse"
                  temporary={true}
                  readOnly={
                    readOnly ||
                    hasAssignments(orderLine as ISalesOrderLine) ||
                    isFullyReceived(orderLine as IPurchaseOrderLine)
                  }
                />
              </Form.Fieldset>
              <Form.Fieldset cols={2 / 4}>
                <Form.Field
                  dataid={`${field}.${rowIndex}.targetDate`}
                  component={input.DateField}
                  label="Date Required"
                  readOnly={readOnly}
                />
              </Form.Fieldset>
              {isUseAcknowledgedDeliveryDate ? (
                <Form.Fieldset cols={2 / 4}>
                  <Form.Field<IDateFieldProps>
                    dataid={`${field}.${rowIndex}.acknowledgedDeliveryDate`}
                    component={input.DateField}
                    label="Acknowledged Delivery Date"
                    readOnly={readOnly}
                  />
                </Form.Fieldset>
              ) : null}
              {type === 'PURCHASE' ? (
                <>
                  <Form.Fieldset cols={2 / 4}>
                    <Form.Field
                      readOnly={true}
                      component={input.BlurTextField}
                      dataid={`${field}.${rowIndex}.totalAdditionalCost`}
                      label="Line Additional Cost"
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={2 / 4}>
                    <Form.Field
                      readOnly={true}
                      component={input.BlurTextField}
                      dataid={`${field}.${rowIndex}.unitAdditionalCost`}
                      label="Unit Additional Cost"
                    />
                  </Form.Fieldset>
                </>
              ) : null}
              {orderLine?.raiseShortage ? (
                <Form.Fieldset cols={1 / 4}>
                  <Form.Field
                    dataid={`${field}.${-rowIndex}.raiseShortage`}
                    component={input.Checkbox}
                    label="Raise Shortage"
                    readOnly={true}
                  />
                </Form.Fieldset>
              ) : null}
            </Form.TabContent>
            <Form.TabContent id="stockLevel" label="Stock Levels" key="stockLevelTab" onlyRenderWhenActive={true}>
              <StockTotals product={product} />
            </Form.TabContent>
            <Form.TabContent id={'stockUsage'} label={'Stock Usage'} key={'stockUsageTab'} onlyRenderWhenActive={true}>
              <div style={{ paddingTop: '1em' }}>
                <ProductWarehouseUsageGrid product={product} warehouse={warehouse} />
              </div>
            </Form.TabContent>
            <Form.TabContent
              id={'itemOrderHistory'}
              label={'Item Order History'}
              key="itemOrderHistoryTab"
              onlyRenderWhenActive={true}
              shouldRender={(connectedForm.values as ISalesOrder)?.orderType === OrderType.Quote}
              tabStyle={{ width: '200px' }}
            >
              <QuoteItemOrderHistoryTabContent product={product} />
            </Form.TabContent>
            <Form.TabContent id="stockLots" label="Bin Locations" key="stockLockTab" onlyRenderWhenActive={true}>
              <GridContainer>
                <ProductsStockLotWorkspace
                  embeddedView={true}
                  additionalVariables={{ excludeProduct: true }}
                  predefinedFilters={stockLotsPredefinedFilters}
                  viewScreenName="/Products/StockLots/Modal"
                  includeGenericSearch={false}
                  precision={product?.precision}
                  columns={stockLotsWorkspaceColumns}
                  nonReplenishable={product?.nonReplenishable}
                />
              </GridContainer>
            </Form.TabContent>
            <Form.TabContent id="history" label="History" shouldRender={!!customer?.id}>
              <CustomerHistory customer={customer} product={product} />
            </Form.TabContent>
            <Form.TabContent id="haggle" label="Haggle">
              <Form.Row>
                <Form.Fieldset cols={1 / 3}>
                  <Legend label={`Price Information`} />
                  <CostPriceInformation
                    orderLine={orderLine as ISalesOrderLine}
                    readOnly={readOnly}
                    fieldPrefix={`${field}.${rowIndex}`}
                    currencyCode={currencyCode}
                  />
                  <MinimumPriceInformation orderLine={orderLine} currencyCode={currencyCode} />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 3}>
                  <Form.Legend label="Discount" />
                  <Form.Row>
                    <Form.Fieldset cols={2 / 4}>
                      <CustomDiscountInput
                        label="Terms"
                        rowDataId={`${field}.${rowIndex}`}
                        fieldId={'termA'}
                        readOnly={pricesReadOnly || disableDiscountTermsEditsForPurchaseOrders}
                        temporarySave={performTemporarySave}
                        inputCols={1 / 2}
                        labelCols={1 / 2}
                        data-testid={'terms-a'}
                      />
                    </Form.Fieldset>
                    <Form.Fieldset cols={1 / 4}>
                      <CustomDiscountInput
                        rowDataId={`${field}.${rowIndex}`}
                        fieldId={'termB'}
                        readOnly={pricesReadOnly || disableDiscountTermsEditsForPurchaseOrders}
                        temporarySave={performTemporarySave}
                        includeLabel={false}
                        data-testid={'terms-b'}
                      />
                    </Form.Fieldset>
                    <Form.Fieldset cols={1 / 4}>
                      <CustomDiscountInput
                        rowDataId={`${field}.${rowIndex}`}
                        fieldId={'termC'}
                        readOnly={pricesReadOnly || disableDiscountTermsEditsForPurchaseOrders}
                        temporarySave={performTemporarySave}
                        includeLabel={false}
                        data-testid={'terms-c'}
                      />
                    </Form.Fieldset>
                  </Form.Row>
                  {orderLines[rowIndex]?.product?.luckinsItem?.id ? (
                    <>
                      <a
                        href={`/Products/Luckins/LuckinsItems/Details/${orderLines[rowIndex]?.product?.luckinsItem?.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <LuckinsHeader>LUCKINS - {orderLines[rowIndex]?.product?.luckinsItem?.itemCode}</LuckinsHeader>
                      </a>
                      <BlurTextField
                        value={`£${orderLines[rowIndex]?.product?.luckinsItem?.tradePrice1?.price} [Quantity Break ${orderLines[rowIndex]?.product?.luckinsItem?.tradePrice1?.priceBreakQuantity}]`}
                        label="Trade Price"
                        readOnly={true}
                        data-testid={'luckins-trade-price'}
                      />
                    </>
                  ) : null}
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 3}>
                  {showCostFieldsPurchasing ? (
                    <>
                      <Form.Legend label={`${type === 'SALES' ? 'Selling' : 'Cost'} Price`} />
                      <BlurTextField
                        data-testid={'cost-price-origin'}
                        value={orderLines[rowIndex]?.priceOriginDescription as string}
                        label="Price Origin"
                        readOnly={true}
                        labelCols={2 / 5}
                        inputCols={3 / 5}
                      />
                      <NettPriceInformation
                        type={type}
                        orderLine={orderLine}
                        currencyCode={currencyCode}
                        labelCols={2 / 5}
                        inputCols={3 / 5}
                      />
                    </>
                  ) : null}
                  {vatInclusive && showCostFieldsPurchasing ? (
                    <NettPriceInformation
                      type={type}
                      orderLine={orderLine}
                      currencyCode={currencyCode}
                      excludingVat={true}
                      labelCols={2 / 5}
                      inputCols={3 / 5}
                    />
                  ) : null}
                  {{}.hasOwnProperty.call(orderLine, 'displayProfitMargin') &&
                  app?.fullUserDetails?.companyUser?.costViewPermission &&
                  app.fullUserDetails.companyUser.costViewPermission !== CostViewPermission.None ? (
                    <>
                      <Form.Field<IBlurTextFieldProps>
                        component={input.BlurTextField}
                        dataid={`${field}.${rowIndex}.displayProfitMargin`}
                        writeDataid={`${field}.${rowIndex}.forceProfitMargin`}
                        label="Margin %"
                        temporary={true}
                        decimals={2}
                        readOnly={pricesReadOnly}
                        labelCols={2 / 5}
                        inputCols={3 / 5}
                        tabIndex={-1}
                        initialized={true}
                      />
                      <Form.Field<IBlurTextFieldProps>
                        component={FluentTextField}
                        dataid={`${field}.${rowIndex}.displayProfitValue`}
                        label="Margin"
                        labelAddon={labelAddon}
                        temporary={true}
                        readOnly={pricesReadOnly}
                        labelCols={2 / 5}
                        inputCols={3 / 5}
                        tabIndex={-1}
                        type={BlurTextFieldType.Currency}
                        initialized={true}
                        prefix={currencySymbol}
                        enableOnChange={true}
                        onChange={(event, value) => {
                          const writeDataId = `${field}.${rowIndex}.forceProfitValue`;
                          connectedForm.change(writeDataId, value);
                        }}
                        suffix={` per ${orderLine?.sellingUnitQuantity} (${orderLine?.sellingUnitDescription})`}
                      />
                      <NettPriceInformation
                        type={type}
                        orderLine={orderLine}
                        currencyCode={currencyCode}
                        excludingVat={true}
                        labelCols={2 / 5}
                        inputCols={3 / 5}
                      />
                    </>
                  ) : null}
                  <LastOrderInformationHaggleTab
                    itemInformation={itemInformation}
                    commonRecord={commonRecord}
                    product={product!}
                    type={type}
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="alternateUnitTab" label="Units">
              <Form.Row>
                <Form.Fieldset cols={1 / 2} collapsed={true}>
                  <Form.Legend label="Weight" />
                  <Form.Row>
                    <Form.Fieldset cols={1 / 6} collapsed={true}>
                      <ColumnLabel label="Quantity" />
                      <input.BlurTextField
                        includeLabel={false}
                        inputCols={1}
                        readOnly={true}
                        value={orderLine?.quantity}
                        decimals={product?.precision || 0}
                        data-testid={'alternate-unit-weight-quantity'}
                      />
                    </Form.Fieldset>
                    <Form.Fieldset cols={1 / 6} collapsed={true}>
                      <ColumnLabel label="Product Weight" />
                      <input.BlurTextField
                        includeLabel={false}
                        inputCols={1}
                        readOnly={true}
                        value={product?.weight || 0}
                        data-testid={'alternate-unit-weight-weight'}
                      />
                    </Form.Fieldset>
                    <Form.Fieldset cols={1 / 6} collapsed={true}>
                      <ColumnLabel label="Packaging Weight" />
                      <input.BlurTextField
                        includeLabel={false}
                        inputCols={1}
                        readOnly={true}
                        value={product?.packagingDimensions?.weight || 0}
                        data-testid={'alternate-unit-weight-packaging'}
                      />
                    </Form.Fieldset>
                    <Form.Fieldset cols={1 / 6} collapsed={true}>
                      <ColumnLabel label="Line Weight" />
                      <input.BlurTextField
                        includeLabel={false}
                        inputCols={1}
                        readOnly={true}
                        value={orderLine?.lineWeight || 0}
                        data-testid={'alternate-unit-weight-line'}
                      />
                    </Form.Fieldset>
                    <Form.Fieldset cols={1 / 6} collapsed={true}>
                      <ColumnLabel label="Line Packaging Weight" />
                      <input.BlurTextField
                        includeLabel={false}
                        inputCols={1}
                        readOnly={true}
                        value={orderLine?.linePackagingWeight || 0}
                        data-testid={'alternate-unit-weight-line-packaging'}
                      />
                    </Form.Fieldset>
                  </Form.Row>
                </Form.Fieldset>
                <VolumeComponent orderLine={orderLine} />
                <Units units={getIn(orderLine, 'potentialUnits')} />
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="vat" label="Line VAT">
              <Form.Row>
                <Form.Fieldset cols={1 / 3}>
                  <Form.Legend label="VAT Rate" />
                  <Form.Field<IDropdownProps>
                    component={input.Dropdown}
                    dataid={`${field}.${rowIndex}.vatProductCategory`}
                    label="Product VAT Category"
                    temporary={true}
                    readOnly={readOnly || disableVatCategoryEditsForPurchaseOrders}
                    initialized={true}
                    reformatLabel={false}
                  />
                  <Form.Field<IBlurTextFieldProps>
                    component={input.BlurTextField}
                    dataid={`${field}.${rowIndex}.lineVatRate.rate`}
                    label="VAT Rate"
                    readOnly={true}
                    inputSuffix={'%'}
                    reformatLabel={false}
                  />
                </Form.Fieldset>
                {showCostFieldsPurchasing ? (
                  <Form.Fieldset cols={1 / 3}>
                    <Form.Legend label="VAT Summary" />
                    <Form.Field<IBlurTextFieldProps>
                      component={input.BlurTextField}
                      dataid={`${field}.${rowIndex}.lineVatSummary.goodsTotal`}
                      label="Goods"
                      readOnly={true}
                      decimals={2}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      component={input.BlurTextField}
                      dataid={`${field}.${rowIndex}.lineVatSummary.vatTotal`}
                      label="VAT"
                      readOnly={true}
                      decimals={2}
                    />
                  </Form.Fieldset>
                ) : null}
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent
              id="productNotesTab"
              label="Product Notes"
              badge={productMemoEditorProps.queryProps.data?.listData?.count}
              type="info"
              tabStyle={{ width: 160 }}
              onlyRenderWhenActive={true}
            >
              <MemoEditor
                noteTypeEntity={ProductNoteType}
                {...productMemoEditorProps}
                path={`${field}.${rowIndex}.product`}
              />
            </Form.TabContent>
            <Form.TabContent
              id="orderAssignments"
              label={`${type === 'SALES' ? 'Purchase' : 'Sales'} Assignments`}
              key="orderAssignments"
              tabStyle={{ width: 160 }}
              shouldRender={!!orderLine && !!(orderLine as ISalesOrderLine | IPurchaseOrderLine).assignments}
            >
              <LineDetailsPanelAssignments orderLine={orderLine} type={type} rowIndex={rowIndex} />
            </Form.TabContent>

            <Form.TabContent
              id="orderAssemblyLineAssignments"
              label={`Assembly Assignments`}
              key="assemblyLineAssignments"
              tabStyle={{ width: 160 }}
              shouldRender={
                !!orderLine &&
                (!!(orderLine as IPurchaseOrderLine).assemblyLineAssignments ||
                  !!(orderLine as ISalesOrderLine).assemblyAssignments)
              }
            >
              <LineDetailsPanelAssemblyAssignments orderLine={orderLine} type={type} rowIndex={rowIndex} />
            </Form.TabContent>
            <Form.TabContent
              id="components"
              label="Components"
              shouldRender={
                !!(
                  (orderType === 'ORDER' || orderType === 'QUOTE') &&
                  product?.productClass &&
                  (product.productClass === ProductClass.Kit || product.productClass === ProductClass.Assembly)
                )
              }
            >
              <ComponentGrid
                components={components}
                field={field}
                rowIndex={rowIndex}
                performTemporarySave={performTemporarySave}
                readOnly={componentsReadOnly}
              />
            </Form.TabContent>
            <Form.TabContent
              id="alternativeProducts"
              label="Alternative Products"
              key="alternativeProductsTab"
              badge={alternativeProducts.length}
              type={'info'}
              shouldRender={!!alternativeProducts.length}
              tabStyle={{ width: 200 }}
            >
              <GridContainer>
                <AlternativeProducts
                  alternativeProducts={alternativeProducts}
                  rowIndex={rowIndex}
                  orderLines={orderLines}
                />
              </GridContainer>
            </Form.TabContent>
            <Form.TabContent
              id="newPurchaseOrder"
              label="Purchase order"
              key="newPurchaseOrderTab"
              type={'info'}
              shouldRender={!!newPurchaseOrder}
            >
              <Form.Field<IPurchaseOrderEditComponentProps>
                dataid={`orderLines.${rowIndex}`}
                component={PurchaseOrderEditComponent}
                newPurchaseOrder={newPurchaseOrder}
              />
            </Form.TabContent>
            <Form.TabContent
              id="allocations"
              label="Allocations"
              shouldRender={type === 'SALES' && (field === 'orderLines' || field === 'invoiceLines')}
              onlyRenderWhenActive={true}
            >
              <AllocationsTabContent
                field={field as 'orderLines' | 'invoiceLines'}
                orderLine={orderLine as ISalesOrderLine | ISalesInvoiceLine}
              />
            </Form.TabContent>
            <Form.TabContent
              id="reservations"
              label="Reservations"
              shouldRender={type === 'SALES' && field === 'orderLines' && product?.productClass === ProductClass.Kit}
              onlyRenderWhenActive={true}
            >
              <ReservationsTabContent orderLine={orderLine as ISalesOrderLine} />
            </Form.TabContent>
            <Form.TabContent
              id="productCustoms"
              label="Customs"
              shouldRender={type === 'SALES' && field === 'orderLines' && isWindsorFrameWorkEnabled}
              onlyRenderWhenActive={true}
            >
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <Form.Legend label={'Customs'} />
                  <Form.Field<ICheckboxProps>
                    component={input.Checkbox}
                    dataid={`${field}.${rowIndex}.product.windsorFrameworkAtRisk`}
                    label="Windsor Framework At Risk"
                    readOnly={true}
                  />
                  <Form.Field<IBlurTextFieldProps>
                    component={input.BlurTextField}
                    dataid={`${field}.${rowIndex}.product.countryOfOrigin.description`}
                    label="Country Of Origin"
                    readOnly={true}
                  />
                  <Form.Field<IBlurTextFieldProps>
                    component={input.BlurTextField}
                    dataid={`${field}.${rowIndex}.product.commodityCode`}
                    label="Commodity Code"
                    readOnly={true}
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
          </Form.TabPanel>
        </Form.Fieldset>
      </Form.Row>
    </ViewContextProvider>
  );
};

export default LineDetailsPanel;
